import { httpsCallable } from "firebase/functions";

import { functions } from "../firebase";

export const DEFAULT_REWARD_SETTINGS = {
  normalPointsPerDollar: 10,
  promotionPointsPerDollar: 50,
  promotionEnabled: false,
  promotionStart: null,
  promotionEnd: null,
  redemptionPointsPerDollar: 1000,
  crossStoreRedemptionPercent: 70,
};

// settings/rewards has no client read rule (see firestore.rules), so the
// live values are fetched through a Cloud Function. Everything below
// stays a pure function operating on whatever settings object it's
// given, so previews can still be computed client-side.
const getRewardSettingsCallable = httpsCallable(
  functions,
  "storeGetRewardSettings"
);

function convertDateToMilliseconds(value) {
  if (!value) return null;

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  const convertedDate = new Date(value);

  if (Number.isNaN(convertedDate.getTime())) {
    return null;
  }

  return convertedDate.getTime();
}

export function prepareRewardSettings(data = {}) {
  return {
    ...DEFAULT_REWARD_SETTINGS,
    ...data,
  };
}

export function isPromotionCurrentlyActive(
  settings,
  currentTime = Date.now()
) {
  if (!settings.promotionEnabled) {
    return false;
  }

  const startTime = convertDateToMilliseconds(settings.promotionStart);
  const endTime = convertDateToMilliseconds(settings.promotionEnd);

  if (startTime && currentTime < startTime) {
    return false;
  }

  if (endTime && currentTime > endTime) {
    return false;
  }

  return true;
}

export function getActivePointsRate(settings) {
  return isPromotionCurrentlyActive(settings)
    ? Number(settings.promotionPointsPerDollar)
    : Number(settings.normalPointsPerDollar);
}

export function convertMoneyToCents(amount) {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount) || numericAmount < 0) {
    throw new Error("Please enter a valid purchase amount.");
  }

  return Math.round(numericAmount * 100);
}

export function calculateEarnedPoints(eligibleAmount, pointsRate) {
  const eligibleAmountCents = convertMoneyToCents(eligibleAmount);

  return Math.floor((eligibleAmountCents * pointsRate) / 100);
}

/**
 * Returns the payout percentage that applies to a redemption.
 * Same-store redemptions always pay out at 100%. Redemptions made at a
 * different store than where the points were earned pay out at
 * settings.crossStoreRedemptionPercent (default 70%, i.e. a 30% reduction).
 */
export function getRedemptionPayoutPercent(settings, isCrossStore) {
  if (!isCrossStore) {
    return 100;
  }

  const crossStorePercent = Number(settings.crossStoreRedemptionPercent);

  if (!Number.isFinite(crossStorePercent) || crossStorePercent < 0) {
    throw new Error("Cross-store redemption rate is not configured correctly.");
  }

  return crossStorePercent;
}

/**
 * Converts a number of points into a redemption payout, in cents, honoring
 * the store's redemption rate and (when redeeming cross-store) the
 * crossStoreRedemptionPercent reduction.
 *
 * Example: 100,000 points at redemptionPointsPerDollar=1000 is worth $100
 * at full value. Redeemed cross-store at 70%, the payout is $70 (7000 cents).
 */
export function calculateRedemptionValueCents({
  points,
  redemptionPointsPerDollar,
  isCrossStore = false,
  crossStorePercent,
}) {
  const numericPoints = Number(points);

  if (!Number.isFinite(numericPoints) || numericPoints <= 0) {
    throw new Error("Please enter a valid number of points to redeem.");
  }

  const rate = Number(redemptionPointsPerDollar);

  if (!Number.isFinite(rate) || rate <= 0) {
    throw new Error("Redemption rate is not configured correctly.");
  }

  const payoutPercent = isCrossStore
    ? Number(crossStorePercent)
    : 100;

  if (!Number.isFinite(payoutPercent) || payoutPercent < 0) {
    throw new Error("Cross-store redemption rate is not configured correctly.");
  }

  const fullValueCents = (numericPoints / rate) * 100;
  const payoutCents = (fullValueCents * payoutPercent) / 100;

  return Math.floor(payoutCents);
}

export async function loadRewardSettings() {
  try {
    const response = await getRewardSettingsCallable();
    return prepareRewardSettings(response.data);
  } catch (error) {
    const message =
      error?.message || "Reward settings could not be loaded.";
    throw new Error(message);
  }
}
